"use client";
import { Paperclip } from "lucide-react";
import { useState } from "react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Badge,
} from "visuais";
import {
  UploadArea,
  AnexoList,
  useAnexosCompleto,
  useAnexoLogic,
  AnexoListWithPreview,
} from "@PN/tabela";

// =================================================================
// Tipos
// =================================================================

interface AnexoPopoverProps {
  rec: number;
  z07Chave?: string;
}

// =================================================================
// Componente
// =================================================================

export function AnexoPopover({ rec, z07Chave }: AnexoPopoverProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Busca anexos usando o hook completo - sempre carrega para mostrar o badge
  const {
    anexos,
    isLoading,
    upload,
    isUploading,
    download,
    delete: deleteAnexo,
  } = useAnexosCompleto(z07Chave || "");

  // Debug: log para verificar se está carregando
  // console.log(`AnexoPopover REC${rec}:`, {
  //   prefixo: z07Chave,
  //   anexos: anexos.length,
  //   isLoading,
  // });

  // Lógica de upload
  const {
    selectedFiles,
    fileDescriptions,
    handleFilesSelected,
    handleFileDescriptionChange,
    clearSelectedFiles,
    prepareUploadPayload,
    handleUploadSuccess,
  } = useAnexoLogic();

  const handlePopoverChange = (open: boolean) => {
    setIsPopoverOpen(open);
  };

  const handleUpload = async () => {
    if (!rec || selectedFiles.length === 0) return;

    const uploadPayloads = prepareUploadPayload(rec, anexos);

    try {
      // Executa uploads sequencialmente
      for (const payload of uploadPayloads) {
        await new Promise<void>((resolve, reject) => {
          upload(payload);
          // Aguarda um pouco entre os uploads
          setTimeout(resolve, 100);
        });
      }

      handleUploadSuccess();
    } catch (error) {
      console.error("Erro no upload:", error);
    }
  };

  return (
    <div className="flex justify-center">
      <Popover open={isPopoverOpen} onOpenChange={handlePopoverChange}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Button
              size="icon"
              variant="ghost"
              className="hover:border-2 border dark:border-primary bg-popover transition-all duration-300 size-8"
            >
              <Paperclip className="size-4" />
            </Button>
            {anexos.length > 0 && (
              <Badge
                variant="secondary"
                className="absolute -top-1 -left-1 size-4 dark:bg-primary bg-sky-300  flex items-center justify-center p-0 text-[8pt] fhd:text-[10pt] qhd:text-[12pt] uhd:text-[14pt]"
              >
                {anexos.length}
              </Badge>
            )}
          </div>
        </PopoverTrigger>

        <PopoverContent className="w-80 p-4" align="end">
          <div className="space-y-4">
            {/* Área de Upload */}
            <UploadArea
              onFilesSelected={handleFilesSelected}
              selectedFiles={selectedFiles}
              fileDescriptions={fileDescriptions}
              onFileDescriptionChange={handleFileDescriptionChange}
              onClearFiles={clearSelectedFiles}
              disabled={!rec}
            />
              {/* Botão de Upload */}
            {selectedFiles.length > 0 && rec && (
              <Button
                onClick={handleUpload}
                disabled={isUploading || !rec}
                className="w-full h-8 text-xs"
              >
                {isUploading
                  ? "Enviando..."
                  : !rec
                  ? "Sem REC válido"
                  : `Enviar ${selectedFiles.length} arquivo${
                      selectedFiles.length !== 1 ? "s" : ""
                    }`}
              </Button>
            )}

            {/* Lista de Anexos */}
            <AnexoListWithPreview
              anexos={anexos}
              onDownload={download}
              onDelete={deleteAnexo}
              isLoading={isLoading}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
